import { useEffect, useMemo, useReducer, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createId } from '../utils/id';
import { GardenContext } from './GardenContext';
import type { GardenContextValue, GroupDragState } from './GardenContext';
import { gardenReducer, initialGardenState } from './gardenReducer';
import { loadPersistedState, useFirestoreSync } from './useFirestoreSync';
import { MIN_ZOOM, MAX_ZOOM, ZOOM_STEP, DEFAULT_ZOOM } from './constants';

function clampZoom(value: number): number {
  return Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);
}

interface GardenProviderProps {
  children: ReactNode;
  uid: string;
}

function GardenProvider({ children, uid }: GardenProviderProps) {
  const [state, dispatch] = useReducer(gardenReducer, initialGardenState);
  const [isStateLoading, setIsStateLoading] = useState(true);
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [armedType, setArmedType] = useState<string | null>(null);
  const [isEditingShape, setIsEditingShape] = useState(false);
  const [groupDrag, setGroupDrag] = useState<GroupDragState | null>(null);
  const [zoom, setZoomState] = useState(DEFAULT_ZOOM);

  useEffect(() => {
    let cancelled = false;
    setIsStateLoading(true);
    loadPersistedState(uid).then((loaded) => {
      if (cancelled) return;
      dispatch({ type: 'LOAD_STATE', state: loaded });
      setIsStateLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  useFirestoreSync(uid, state, !isStateLoading);

  const addElement = useCallback((elementType: string, x: number, y: number) => {
    dispatch({ type: 'ADD_ELEMENT', elementType, x, y });
  }, []);

  const moveElement = useCallback((id: string, x: number, y: number) => {
    dispatch({ type: 'MOVE_ELEMENT', id, x, y });
  }, []);

  const resizeElement = useCallback((id: string, width: number, height: number) => {
    dispatch({ type: 'RESIZE_ELEMENT', id, width, height });
  }, []);

  const renameElement = useCallback((id: string, name: string) => {
    dispatch({ type: 'RENAME_ELEMENT', id, name });
  }, []);

  const deleteElement = useCallback((id: string) => {
    dispatch({ type: 'DELETE_ELEMENT', id });
    setSelectedElementIds((current) => current.filter((selectedId) => selectedId !== id));
  }, []);

  const deleteElements = useCallback((ids: string[]) => {
    dispatch({ type: 'DELETE_ELEMENTS_BULK', ids });
    const idSet = new Set(ids);
    setSelectedElementIds((current) => current.filter((selectedId) => !idSet.has(selectedId)));
  }, []);

  const addActionEntry = useCallback((elementId: string, date: string, note: string) => {
    dispatch({ type: 'ADD_ACTION_ENTRY', elementId, entry: { id: createId(), date, note } });
  }, []);

  const addActionEntryBulk = useCallback((elementIds: string[], date: string, note: string) => {
    dispatch({ type: 'ADD_ACTION_ENTRY_BULK', elementIds, date, note });
  }, []);

  const updateActionEntry = useCallback(
    (elementId: string, entryId: string, date: string, note: string) => {
      dispatch({ type: 'UPDATE_ACTION_ENTRY', elementId, entryId, date, note });
    },
    [],
  );

  const deleteActionEntry = useCallback((elementId: string, entryId: string) => {
    dispatch({ type: 'DELETE_ACTION_ENTRY', elementId, entryId });
  }, []);

  const selectElement = useCallback((id: string | null, isCtrl?: boolean) => {
    setSelectedElementIds((current) => {
      if (id === null) return [];
      if (isCtrl) {
        return current.includes(id)
          ? current.filter((selectedId) => selectedId !== id)
          : [...current, id];
      }
      return [id];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedElementIds([]);
  }, []);

  const armType = useCallback((type: string) => {
    setArmedType((current) => (current === type ? null : type));
  }, []);

  const clearArmedType = useCallback(() => {
    setArmedType(null);
  }, []);

  const toggleEditingShape = useCallback(() => {
    setIsEditingShape((current) => !current);
  }, []);

  const updateShapePoint = useCallback((id: string, x: number, y: number) => {
    dispatch({ type: 'UPDATE_SHAPE_POINT', id, x, y });
  }, []);

  const insertShapePoint = useCallback((index: number, x: number, y: number) => {
    dispatch({ type: 'INSERT_SHAPE_POINT', index, point: { id: createId(), x, y } });
  }, []);

  const removeShapePoint = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_SHAPE_POINT', id });
  }, []);

  const beginGroupDrag = useCallback(
    (ids: string[]) => {
      const startPositions: GroupDragState['startPositions'] = {};
      state.elements.forEach((el) => {
        if (ids.includes(el.id)) startPositions[el.id] = { x: el.x, y: el.y };
      });
      setGroupDrag({ ids, startPositions, dx: 0, dy: 0 });
    },
    [state.elements],
  );

  const updateGroupDrag = useCallback((dx: number, dy: number) => {
    setGroupDrag((current) => (current ? { ...current, dx, dy } : current));
  }, []);

  const commitGroupDrag = useCallback(() => {
    setGroupDrag((current) => {
      if (current) {
        const moves = current.ids.map((id) => ({
          id,
          x: current.startPositions[id].x + current.dx,
          y: current.startPositions[id].y + current.dy,
        }));
        dispatch({ type: 'MOVE_ELEMENTS_BULK', moves });
      }
      return null;
    });
  }, []);

  const cancelGroupDrag = useCallback(() => {
    setGroupDrag(null);
  }, []);

  const setZoom = useCallback((value: number) => {
    setZoomState(clampZoom(value));
  }, []);

  const zoomIn = useCallback(() => {
    setZoomState((current) => clampZoom(current + ZOOM_STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomState((current) => clampZoom(current - ZOOM_STEP));
  }, []);

  const resetZoom = useCallback(() => {
    setZoomState(DEFAULT_ZOOM);
  }, []);

  const value = useMemo<GardenContextValue>(
    () => ({
      elements: state.elements,
      shape: state.shape,
      selectedElementIds,
      armedType,
      isEditingShape,
      groupDrag,
      zoom,
      addElement,
      moveElement,
      resizeElement,
      renameElement,
      deleteElement,
      deleteElements,
      addActionEntry,
      addActionEntryBulk,
      updateActionEntry,
      deleteActionEntry,
      selectElement,
      clearSelection,
      armType,
      clearArmedType,
      toggleEditingShape,
      updateShapePoint,
      insertShapePoint,
      removeShapePoint,
      beginGroupDrag,
      updateGroupDrag,
      commitGroupDrag,
      cancelGroupDrag,
      setZoom,
      zoomIn,
      zoomOut,
      resetZoom,
    }),
    [
      state.elements,
      state.shape,
      selectedElementIds,
      armedType,
      isEditingShape,
      groupDrag,
      zoom,
      addElement,
      moveElement,
      resizeElement,
      renameElement,
      deleteElement,
      deleteElements,
      addActionEntry,
      addActionEntryBulk,
      updateActionEntry,
      deleteActionEntry,
      selectElement,
      clearSelection,
      armType,
      clearArmedType,
      toggleEditingShape,
      updateShapePoint,
      insertShapePoint,
      removeShapePoint,
      beginGroupDrag,
      updateGroupDrag,
      commitGroupDrag,
      cancelGroupDrag,
      setZoom,
      zoomIn,
      zoomOut,
      resetZoom,
    ],
  );

  if (isStateLoading) {
    return <div className="App-loading">Loading your garden…</div>;
  }

  return <GardenContext.Provider value={value}>{children}</GardenContext.Provider>;
}

export default GardenProvider;
